import { constants, http } from 'lib_ui-services';

// Injected so the HTTP call can be replaced in tests without a network stub.
const _p = { post: (...args) => http.post(...args) };
export const _private = _p;

/**
 * The "Recreate Order" button (#1909).
 *
 * Order resuscitation: an order that went wrong is archived and a clean copy takes its
 * place under the same order number, so an operator does not re-key the manifest and the
 * WMS keeps polling the number it already knows.
 *
 * The work is deliberately server-side: allocating the archive name and creating the clone
 * have to be coordinated, and only the API can undo the rename if the clone insert fails.
 * That undo is a compensating rollback rather than a transaction — the steps are not atomic,
 * and this rule should not be written as though they were.
 *
 * Items are NOT unlinked. A retired order keeps its memberships on purpose: they are the
 * record of what happened on that attempt, which the retired-copies report reads. They are
 * reactivated so they can be scanned onto the replacement.
 *
 * This rule is the button. It invokes the API and opens the replacement.
 *
 * Archiving is not reversible from the UI, so this rule asks first.
 *
 * The confirmation used to be described as the button's job. It is not one a button can do:
 * `actionElement/Button.js` has no confirm support and the handoff's button JSON carries no
 * such property, so nothing was asking — one click archived a live order's predecessor with no
 * way back. The prompt is raised here instead, the way doingClearCache_application_runtime does,
 * which also means the API and the keyboard reach the same guard as the mouse.
 */
export default {
    verb: 'doingRecreate',
    namespace: 'order',
    relation: 'order',
    description: 'Archive the active order and open its clean replacement',
    featureFlag: 'orderConfirmation_enabled',
    logic
};

async function logic({ data, context, dispatch }) {
    const record = data?.record ?? data?.activeRecord?.record ?? data?.newRecord;
    const title = record?.title;

    if (!title) {
        popError(dispatch, 'Select an order before recreating it.');
        return;
    }

    if (!(await _confirmRecreate(dispatch, title))) return;

    let response;
    try {
        // The title can contain characters that are not URL-safe; order numbers are
        // customer-supplied strings, not identifiers we mint.
        // The empty body is required, not decorative: http.post runs removeMetaData(data)
        // before it builds the request, and that dereferences `data.$loki` — so calling with
        // only a URL throws a TypeError before any fetch happens, and this bodyless action
        // would report a failure every single time.
        response = await _p.post(`/api/order/order/${encodeURIComponent(title)}/copy`, {});
    } catch (error) {
        // The server distinguishes "no such order" from "someone else archived it first";
        // surfacing its message keeps that distinction rather than flattening it.
        popError(dispatch, error?.message || `Could not recreate order '${title}'.`);
        return;
    }

    // A 202 is not a result. `http.post` hands back the string 'Accepted - No Content' when the
    // request was accepted but the body is still coming, so reading `.archivedTitle` off it gave
    // "The previous attempt is archived as undefined." — and there is no clone to open either.
    const order = typeof response === 'object' ? response?.order : undefined;
    const archivedTitle = typeof response === 'object' ? response?.archivedTitle : undefined;

    dispatch(
        {
            message: archivedTitle
                ? `Order ${title} was recreated. The previous attempt is archived as ${archivedTitle}.`
                : `Order ${title} is being recreated.`,
            timeout: 5000,
            variant: 'info',
            type: constants.notificationTypes.TOAST
        },
        { verb: 'pop', namespace: 'application', relation: 'notification' }
    );

    if (!order?._id) return;

    // Open the replacement, so the operator lands on the order they are about to scan rather
    // than on the archived one they just retired.
    //
    // DISPATCHED, not returned. A rule's return value is published as a success event for its
    // own verb — here `recreate` — and ActiveRecord listens only for copy, edit, view, new,
    // cancel, submit and remove. So `activationVerb: 'edit'` on this return was read by nobody:
    // the operator stayed on the order they had just archived, which by then was renamed and
    // inactive, and the only sign anything had happened was the toast. Raising an `edit` of its
    // own is the same move doingFind_order_order makes to open an order it has just located.
    // The whole record, not just its id. `doingEdit` resolves an id against the LOCAL database
    // and does not fall back to the server, and the clone only arrives there through socket sync
    // — which runs separately from this HTTP reply. Whenever the reply wins that race, an id-only
    // dispatch threw "No record exists with id …" immediately after the success toast. The server
    // already returns the clone, so handing it over removes the race rather than narrowing it.
    await dispatch(
        { record: order },
        { ...context, verb: 'edit', namespace: 'order', relation: 'order' },
        true
    );
}

/**
 * Ask before archiving. Resolves false when the operator backs out.
 */
function _confirmRecreate(dispatch, title) {
    return new Promise(resolve => {
        dispatch(
            {
                message:
                    `This retires order ${title} and starts a replacement under the same number. ` +
                    'The retired copy stays available for reporting, but it cannot be brought back. Continue?',
                okButtonText: 'RECREATE',
                icon: 'warning',
                iconColorStyle: constants.COLOR_STYLES.Warning,
                variant: constants.VARIANT.Error,
                okAction: () => resolve(true),
                cancelAction: () => resolve(false)
            },
            { verb: 'confirm', namespace: 'application', relation: 'user' }
        );
    });
}

function popError(dispatch, message) {
    dispatch(
        {
            message,
            timeout: 5000,
            // `error`, not `info`. A failed recreate was shown in the same neutral styling as a
            // successful one, so the one toast an operator most needs to notice looked exactly
            // like the one they were expecting.
            variant: 'error',
            type: constants.notificationTypes.TOAST
        },
        { verb: 'pop', namespace: 'application', relation: 'notification' }
    );
}
